import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { WorkOrderPriceRevisionTypeEnum } from 'gql/graphql';
import type {
    BimChapterEnum,
    BimPriceLevelEnum,
    BimWorkOrderChangePriceRowData,
    BimWorkOrderPriceValue,
} from 'gql/graphql';

import type { IGetRowsParams } from 'ag-grid-community';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
    BimWorkOrderRowDetailsType,
    BimWorkOrderRowTypeEnum,
    bimWorkOrderTotalsChapterLabel,
} from '../../../WorkOrder/types';
import { useBimWorkOrderChangeTotals } from '../../hooks';
import { bimWorkOrderChangePriceDetailsGql, bimWorkOrderChangeRevisionsGql } from '../bimWorkOrderChangePriceQueries';
import type { TDetailsData } from '../components/cellRenderers';

interface IBimWorkOrderChangePriceDetails {
    checkedPriceRow: TDetailsData['data'] | null | undefined;
    detailsType: BimWorkOrderRowDetailsType | undefined;
    detailsRowType: TDetailsData['rowType'] | undefined;
    chapter: BimChapterEnum | undefined;
    workOrderChangeId: number | undefined;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | undefined;
    constructionTypeId: number | undefined;
    priceLevel: BimPriceLevelEnum | undefined;
}

export type TGroupedItem =
    | { planValue: { name: string | undefined | null }; isGroupTitle: boolean }
    | (BimWorkOrderChangePriceRowData & { planValue: BimWorkOrderPriceValue });

type TGroupedItemsObj = {
    [key: string]: TGroupedItem[];
};

export const useBimWorkOrderChangePriceDetailsData = ({
    checkedPriceRow,
    detailsType,
    detailsRowType,
    chapter,
    workOrderChangeId,
    workOrderRootObjectId,
    workOrderChangeRootObjectId,
    constructionTypeId,
    priceLevel,
}: IBimWorkOrderChangePriceDetails) => {
    const dispatch = useDispatch();
    /*
     * состав сметы
     */
    const detailsVariables = useMemo(() => {
        if (
            checkedPriceRow &&
            detailsType === BimWorkOrderRowDetailsType.Details &&
            'workOrderPriceId' in checkedPriceRow &&
            detailsRowType === BimWorkOrderRowTypeEnum.RegularString
        ) {
            return {
                workOrderPriceId: checkedPriceRow?.workOrderPriceId,
                workOrderChangePriceId: checkedPriceRow?.workOrderChangePriceId,
                priceLevel,
            };
        }

        return undefined;
    }, [checkedPriceRow, detailsRowType, detailsType, priceLevel]);

    const [resetRowDetailsData] = useResetStoreData({
        requestId: 'bimWorkOrderChangePriceDetails',
    });

    useEffect(() => {
        if (!detailsVariables) {
            resetRowDetailsData(undefined);
        }
    }, [detailsVariables, resetRowDetailsData]);

    const [priceDetailsData, detailsStatus] = useRequest({
        requestId: 'bimWorkOrderChangePriceDetails',
        query: bimWorkOrderChangePriceDetailsGql,
        variables: detailsVariables,
        withEffect: !!detailsVariables,
    });

    const rowDetailsData = useMemo(() => {
        const groupedItems = priceDetailsData?.bimWorkOrderChangePriceRows?.items?.reduce(
            (acc: TGroupedItemsObj, item) => {
                const sectionName = item?.sectionName;

                if (!sectionName) {
                    return acc;
                }

                if (!acc[sectionName]) {
                    acc[sectionName] = [
                        {
                            planValue: { name: sectionName },
                            isGroupTitle: true,
                        },
                    ];
                }

                acc[sectionName].push({ ...item, planValue: { ...item?.value } });

                return acc;
            },
            {},
        );

        return Object.values(groupedItems ?? {}).flat();
    }, [priceDetailsData?.bimWorkOrderChangePriceRows?.items]);

    /*
     * ревизии/обновления сметы
     */
    const getRevisionRowParams = useRef<IGetRowsParams>();

    const revisionVariables = useMemo(() => {
        if (
            checkedPriceRow &&
            detailsType === BimWorkOrderRowDetailsType.Update &&
            'workOrderPriceId' in checkedPriceRow &&
            detailsRowType === BimWorkOrderRowTypeEnum.RegularString
        ) {
            return {
                workOrderPriceId: checkedPriceRow?.workOrderPriceId,
                workOrderChangePriceId: checkedPriceRow?.workOrderChangePriceId,
                revisionType: WorkOrderPriceRevisionTypeEnum.Update,
                priceLevel,
            };
        }

        if (
            checkedPriceRow &&
            detailsType === BimWorkOrderRowDetailsType.Revision &&
            'workOrderPriceId' in checkedPriceRow
        ) {
            return {
                workOrderPriceId: checkedPriceRow?.workOrderPriceId,
                workOrderChangePriceId: checkedPriceRow?.workOrderChangePriceId,
                revisionType: WorkOrderPriceRevisionTypeEnum.Revision,
                priceLevel,
            };
        }

        return undefined;
    }, [checkedPriceRow, detailsRowType, detailsType, priceLevel]);

    const [resetRowRevisionData] = useResetStoreData({
        requestId: 'bimWorkOrderChangePriceRevisions',
    });

    const [revisionsData, , , revisionsRequest] = useRequest({
        requestId: 'bimWorkOrderChangePriceRevisions',
        query: bimWorkOrderChangeRevisionsGql,
        withEffect: false,
    });

    useEffect(() => {
        if (revisionsData?.bimWorkOrderChangePriceRevisions?.items && getRevisionRowParams.current) {
            getRevisionRowParams.current.successCallback(
                revisionsData?.bimWorkOrderChangePriceRevisions?.items,
                revisionsData?.bimWorkOrderChangePriceRevisions?.totalCount,
            );
        }
    }, [
        revisionsData?.bimWorkOrderChangePriceRevisions?.items,
        revisionsData?.bimWorkOrderChangePriceRevisions?.totalCount,
    ]);

    const rowRevisionDataSource = useMemo(() => {
        if (!revisionVariables) {
            return undefined;
        }

        return {
            rowCount: undefined,

            getRows: (params: IGetRowsParams) => {
                dispatch(
                    revisionsRequest({
                        ...revisionVariables,
                        take: 50,
                        skip: params.startRow,
                    }),
                );

                getRevisionRowParams.current = params;
            },
            destroy() {
                resetRowRevisionData(undefined);
            },
        };
    }, [dispatch, resetRowRevisionData, revisionVariables, revisionsRequest]);

    /*
     * детализация итогов
     */
    const { rowTotalsData: totalRowDetails, isLoading: isTotalDetailsLoading } = useBimWorkOrderChangeTotals({
        workOrderChangeId,
        chapter,
        workOrderObjectId: workOrderRootObjectId,
        workOrderChangeObjectId: workOrderChangeRootObjectId,
        constructionTypeId,
        useInPrices: false,
        detailsRowType,
        priceLevel,
    });

    /*
     * общая информация
     */
    const detailsBaseRow = useMemo(() => {
        if (detailsRowType === BimWorkOrderRowTypeEnum.TotalsString) {
            return undefined;
        }

        return detailsType && detailsType !== BimWorkOrderRowDetailsType.Update
            ? [{ ...checkedPriceRow, topPinnedRow: true }]
            : undefined;
    }, [checkedPriceRow, detailsRowType, detailsType]);

    const isLoading = (detailsVariables && detailsStatus === RequestStatus.FETCHING) || isTotalDetailsLoading;

    const detailsTitle = useMemo(() => {
        if (detailsType === BimWorkOrderRowDetailsType.Update) {
            return `Обновления по смете ${checkedPriceRow?.planValue?.code}`;
        }
        if (detailsType === BimWorkOrderRowDetailsType.Revision) {
            return `Ревизии ${checkedPriceRow?.planValue?.code}`;
        }
        if (detailsRowType === BimWorkOrderRowTypeEnum.TotalsString) {
            return `итоги ${
                chapter ? bimWorkOrderTotalsChapterLabel[chapter as keyof typeof bimWorkOrderTotalsChapterLabel] : ''
            }`;
        }

        return 'Состав сметы';
    }, [chapter, checkedPriceRow?.planValue?.code, detailsRowType, detailsType]);

    const detailsData = useMemo(() => {
        if (detailsRowType === BimWorkOrderRowTypeEnum.TotalsString) {
            return totalRowDetails;
        }

        return rowDetailsData;
    }, [detailsRowType, rowDetailsData, totalRowDetails]);

    return {
        detailsData,
        rowRevisionDataSource,
        detailsBaseRow,
        isDetailsLoading: isLoading,
        detailsTitle,
    };
};
